import { AnimatedHeight } from "@components/animated-side/height";
import { CoinsVal } from "@components/icons/coins";
import { QualityChoiceModal } from "@components/quality-choice-modal";
import { ResponsiveModal } from "@components/responsive-modal";
import UpgradeNeeded from "@components/upgrade-needed";
import { useDragAndDrop } from "@features/loras/use-drag-and-drop";
import { useFileInput } from "@features/loras/use-lora-file-input";
import { useLoras } from "@features/loras/use-loras";
import {
  UploadedPhotoType,
  useUploadPhotos,
} from "@features/loras/use-upload-photos";
import { useHideCosts, useUser } from "@features/user/use-user";
import { Box, Button, Heading, Link, Text, TextField } from "@radix-ui/themes";
import _ from "lodash";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import {
  EmptyState,
  PhotoGrid,
  PhotoThumbnail,
} from "./step-1-components/photo-grid";
import { ProgressBar } from "./step-1-components/progress-bar";
import { Guidelines, Title } from "./step-1-components/title";
import { StepImport } from "./step-import";

interface Step1Props {
  type: "style" | "model";
  initialName: string;
  initialImages?: UploadedPhotoType[];
  upgrade?: boolean;
}

export const Step1 = ({ ...props }: Step1Props) => {
  const { t } = useTranslation();
  const { create } = useLoras();
  const { site } = useParams();
  const navigate = useNavigate();
  const { credits } = useUser();
  const enforceAgileModel = credits < 400;
  const hideCosts = useHideCosts(); // New users should not be bothered with costs yet

  const [importOpen, setImportOpen] = useState(false);
  const [qualityChoiceOpen, setQualityChoiceOpen] = useState(false);
  const [createAnywayOpen, setCreateAnywayOpen] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [name, setName] = useState(props.initialName);
  const [model, setModel] = useState<"agile" | "expression" | "none">("none");
  const [progressGuides, setProgressGuides] = useState<string[]>([]);

  useEffect(() => {
    posthog.capture("reached_lora_create", {});
  }, []);

  // Use the existing custom hook
  const { subtype, loading, photos, handleFilesChange, removePhoto } =
    useUploadPhotos(props.type, props.initialImages);

  // Drag and drop functionality
  const { dragActive, handleDrag, handleDrop } =
    useDragAndDrop(handleFilesChange);

  // File input functionality
  const { openFileDialog, handleFileInputChange } =
    useFileInput(handleFilesChange);

  // Memoized photo filtering to avoid recalculation on every render
  const { goodPhotos, rejectedPhotos, uploadingPhotos } = useMemo(() => {
    return {
      goodPhotos: photos.filter((p) => p.id && p.score > 0),
      rejectedPhotos: photos.filter((p) => p.id && p.score <= 0 && p.url),
      uploadingPhotos: photos.filter((p) => !p.id),
    };
  }, [photos]);

  // Memoized minimum photo count
  const minPhotoCount = useMemo(() => {
    return subtype === "car" || subtype === "object" ? 10 : 10;
  }, [subtype]);

  let effectiveModel = model;
  if (model === "expression" && enforceAgileModel) {
    effectiveModel = "agile";
  }

  const createLora = useCallback(
    async (preferedModel: "agile" | "expression") => {
      const technology =
        effectiveModel === "agile" || preferedModel === "agile"
          ? "images"
          : "lora";

      // Show quality choice modal if creating agile model with avatar subtype
      if (technology === "images" && subtype === "avatar") {
        setQualityChoiceOpen(true);
        return;
      }

      setLoadingCreate(true);
      try {
        if (subtype === "none") throw new Error("Subtype must be selected");
        const main = _.sortBy(
          goodPhotos,
          (a) => -(a.details?.main_score || a.score),
        )[0].id!;
        const lora = await create(
          main,
          goodPhotos.map((p) => p.id!),
          {
            name,
            type: props.type,
            subtype: props.type === "style" ? "style" : subtype,
            pro: false,
            technology,
          },
        );
        navigate(`/${site}/lora/${lora.id}`);
      } catch (e) {
        console.error("Failed to create LoRA:", e);
        toast.error(t("loras.upload.creation_failed"));
      } finally {
        setLoadingCreate(false);
      }
    },
    [
      name,
      navigate,
      site,
      create,
      props.type,
      t,
      subtype,
      goodPhotos,
      effectiveModel,
    ],
  );

  return (
    <div className="space-y-2">
      <Title
        name={name}
        {...props}
        subtype={props.type === "style" ? "style" : subtype}
      />
      <Guidelines
        {...props}
        subtype={props.type === "style" ? "style" : subtype}
      />

      {/* Main Drop Zone */}
      <div>
        <div
          className={twMerge(
            "relative border border-b-0 border-dashed rounded-t-md p-4 text-center transition-colors",
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <AnimatedHeight>
            {goodPhotos.length === 0 && uploadingPhotos.length === 0 ? (
              <EmptyState
                onUploadClick={openFileDialog}
                loading={loading || loadingCreate}
              />
            ) : (
              <PhotoGrid
                goodPhotos={goodPhotos}
                uploadingPhotos={uploadingPhotos}
                onRemove={removePhoto}
                onUploadClick={openFileDialog}
                loading={loading}
              />
            )}
          </AnimatedHeight>
        </div>
        <AnimatedHeight>
          <div className="pb-4">
            <ProgressBar
              photos={goodPhotos}
              subtype={props.type === "style" ? "style" : subtype}
              minPhotoCount={minPhotoCount}
              onAvailableModelChange={setModel}
              onGuidesChange={setProgressGuides}
              upgrade={props.upgrade}
            />
          </div>
        </AnimatedHeight>
      </div>
      <div>
        {/* Rejected Photos Section */}
        <AnimatedHeight>
          {rejectedPhotos.length > 0 && (
            <div className="space-y-3">
              <Text size="3" weight="medium" color="orange">
                {t("loras.upload.rejected_photos", {
                  count: rejectedPhotos.length,
                })}
              </Text>
              <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 lg:grid-cols-12 gap-2">
                {rejectedPhotos.map((photo) => (
                  <PhotoThumbnail
                    key={photo.front_id}
                    photo={photo}
                    onRemove={() => removePhoto(photo.front_id)}
                    isRejected
                  />
                ))}
              </div>
            </div>
          )}
        </AnimatedHeight>

        {/* Hidden file input */}
        <input
          id="file-input"
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={handleFileInputChange}
        />

        <AnimatedHeight>
          {(!model || model === "none" || subtype === "none") && (
            <Link
              href="#"
              className="mt-4"
              onClick={() => {
                setImportOpen(true);
              }}
            >
              {t("loras.type.importLink")}
            </Link>
          )}

          {!!model &&
            model === "none" &&
            subtype !== "none" &&
            (goodPhotos?.length || 0) >= 10 && (
              <>
                <Box className="space-y-2 py-4">
                  <Heading size="2">
                    {t("loras.upload.create_anyway_title")}
                  </Heading>
                  <Text size="2" color="gray">
                    {t("loras.upload.create_anyway_description")}
                  </Text>
                </Box>
                <div className="flex flex-row items-center space-x-2">
                  <TextField.Root
                    className="max-w-sm w-full"
                    placeholder={t("loras.type.name", "Nom")}
                    size="2"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loadingCreate}
                  />
                  <div>
                    <UpgradeNeeded minimumCredits={400}>
                      <Button
                        variant="outline"
                        color="orange"
                        disabled={loadingCreate || loading || !name}
                        onClick={() => setCreateAnywayOpen(true)}
                      >
                        {t("loras.upload.create_anyway_button")}
                      </Button>
                    </UpgradeNeeded>
                  </div>
                </div>
              </>
            )}

          {!!model && model !== "none" && subtype !== "none" && (
            <>
              <Box className="space-y-2 py-4">
                {subtype === "avatar" && model === "agile" ? (
                  <Heading size="2">
                    {t("loras.upload.continue_low_quality")}
                  </Heading>
                ) : (
                  <Heading size="2">
                    {t("loras.upload.ready_high_quality")}
                  </Heading>
                )}
              </Box>

              {/* Continue Button */}
              <div className="">
                <div className="flex flex-row items-center space-x-2">
                  <TextField.Root
                    className="max-w-sm w-full"
                    placeholder={t("loras.type.name", "Nom")}
                    size="2"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loadingCreate}
                  />{" "}
                  <div>
                    <UpgradeNeeded
                      minimumCredits={effectiveModel === "agile" ? 100 : 400}
                    >
                      <Button
                        type="submit"
                        className={twMerge("px-4", !!name && "hue-animation")}
                        disabled={loadingCreate || loading || !name}
                        loading={loadingCreate}
                        onClick={() => createLora(model)}
                      >
                        {t("loras.upload.continue")}{" "}
                        {!hideCosts && (
                          <CoinsVal
                            val={effectiveModel === "agile" ? 100 : 400}
                          />
                        )}
                      </Button>
                    </UpgradeNeeded>
                  </div>
                </div>
              </div>
            </>
          )}

          {!enforceAgileModel && model === "expression" && (
            <div className="">
              <UpgradeNeeded minimumCredits={100}>
                <Link
                  className={twMerge(
                    "block",
                    (loadingCreate || loading || !name) &&
                      "pointer-events-none opacity-50 text-gray-400",
                  )}
                  href="#"
                  onClick={async () => {
                    createLora("agile");
                  }}
                >
                  {t("loras.upload.create_standard_instead")}{" "}
                  <CoinsVal val={100} />
                </Link>
              </UpgradeNeeded>
            </div>
          )}
        </AnimatedHeight>
      </div>

      <StepImport
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        initialName={name}
      />

      <QualityChoiceModal
        isOpen={qualityChoiceOpen}
        onClose={() => setQualityChoiceOpen(false)}
        onContinueStandard={async () => {
          setLoadingCreate(true);
          try {
            if (subtype === "none") throw new Error("Subtype must be selected");
            const main = _.sortBy(
              goodPhotos,
              (a) => -(a.details?.main_score || a.score),
            )[0].id!;
            const lora = await create(
              main,
              goodPhotos.map((p) => p.id!),
              {
                name,
                type: props.type,
                subtype: props.type === "style" ? "style" : subtype,
                pro: false,
                technology: "images",
              },
            );
            navigate(`/${site}/lora/${lora.id}`);
          } catch (e) {
            console.error("Failed to create LoRA:", e);
            toast.error(t("loras.upload.creation_failed"));
          } finally {
            setLoadingCreate(false);
          }
        }}
        onAddMorePictures={() => {
          // User wants to add more pictures to reach expression quality
          // Just close the modal, they can add more photos
          setQualityChoiceOpen(false);
        }}
        onUpgrade={() => {
          // Navigate to billing page
          navigate(`/${site}/billing`);
        }}
      />

      {/* Create Anyway Confirmation Dialog */}
      <ResponsiveModal
        isOpen={createAnywayOpen}
        onClose={() => setCreateAnywayOpen(false)}
        title={t("loras.upload.create_anyway_dialog.title")}
        height="auto"
      >
        <div className="space-y-4">
          <Text size="3">{t("loras.upload.create_anyway_dialog.warning")}</Text>

          {progressGuides.length > 0 && (
            <Box className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg space-y-2">
              <Text size="2" weight="bold" color="orange">
                {t("loras.upload.create_anyway_dialog.suggestions")}
              </Text>
              <ul className="list-disc pl-5 space-y-1">
                {progressGuides.map((guide, idx) => {
                  const getTranslationKey = (guideKey: string) =>
                    `loras.upload.progress.guides.${guideKey}`;
                  const message = (() => {
                    switch (guide) {
                      case "enough_photos":
                        return t(getTranslationKey("enough_photos"), {
                          current: goodPhotos.length,
                          needed: minPhotoCount,
                        });
                      default:
                        return t(getTranslationKey(guide));
                    }
                  })();
                  return (
                    <li key={idx}>
                      <Text size="2" color="gray">
                        {message}
                      </Text>
                    </li>
                  );
                })}
              </ul>
            </Box>
          )}

          <div className="flex flex-row gap-2 justify-end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setCreateAnywayOpen(false)}
              disabled={loadingCreate}
            >
              {t("general.cancel")}
            </Button>
            <Button
              variant="solid"
              color="orange"
              onClick={async () => {
                setCreateAnywayOpen(false);
                await createLora(subtype === "avatar" ? "expression" : "agile");
              }}
              disabled={loadingCreate}
              loading={loadingCreate}
            >
              {t("loras.upload.create_anyway_dialog.confirm")}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};
